import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Building2, MonitorSmartphone, BellRing, Ticket, ShieldCheck,
  Workflow, ScrollText, Package, Radar, FileBarChart, Plug, Users, History,
  Search, Command as CmdIcon, Sparkles, LogOut, Bot, Activity, Boxes, ShieldAlert, BookOpen, Menu,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { CommandPalette } from "@/components/CommandPalette";
import { CopilotDrawer } from "@/components/CopilotDrawer";
import { MfaSetupModal } from "@/components/MfaSetupModal";
import { TimerBanner } from "@/components/TimerBanner";
import { initLive } from "@/lib/live";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, perm: "devices:view" },
  { to: "/customers", label: "Customers", icon: Building2, perm: "customers:view" },
  { to: "/devices", label: "Devices", icon: MonitorSmartphone, perm: "devices:view" },
  { to: "/assets", label: "Assets", icon: Boxes, perm: "devices:view" },
  { to: "/people", label: "People", icon: Users, perm: "devices:view" },
  { to: "/alerts", label: "Alerts", icon: BellRing, perm: "alerts:view" },
  { to: "/tickets", label: "Tickets", icon: Ticket, perm: "tickets:view" },
  { to: "/knowledge-base", label: "Knowledge Base", icon: BookOpen, perm: "tickets:view" },
  { to: "/patches", label: "Patch Management", icon: ShieldCheck, perm: "patch:view" },
  { to: "/automation", label: "Automation", icon: Workflow, perm: "policies:view" },
  { to: "/scripts", label: "Scripts", icon: ScrollText, perm: "scripts:view" },
  { to: "/software", label: "Software", icon: Package, perm: "software:view" },
  { to: "/network-discovery", label: "Network Discovery", icon: Radar, perm: "devices:view" },
  { to: "/monitors", label: "Uptime Monitors", icon: Activity, perm: "devices:view" },
  { to: "/reports", label: "Reports", icon: FileBarChart, perm: "devices:view" },
  { to: "/integrations", label: "Integrations", icon: Plug, perm: "admin:integrations" },
  { to: "/admin", label: "Administration", icon: Users, perm: "admin:users" },
  { to: "/audit", label: "Audit Log", icon: History, perm: "admin:audit" },
];

export function Layout({ children }) {
  const { user, setUser, logout, can } = useAuth();
  const nav = useNavigate();
  const staff = user?.all_customers || user?.role === "mimoit_admin";
  const visibleNav = NAV.filter((n) => staff || (n.perm ? can(n.perm) : false));
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [copilotOpen, setCopilotOpen] = useState(false);
  const [mfaOpen, setMfaOpen] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  const [alertCount, setAlertCount] = useState(0);
  const refreshUser = () => api.get("/auth/me").then((r) => setUser(r.data)).catch(() => {});

  useEffect(() => {
    initLive();
    const load = () => api.get("/alerts").then((r) => setAlertCount(r.data.length)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setPaletteOpen(true); }
    };
    window.addEventListener("keydown", onKey);
    return () => { clearInterval(t); window.removeEventListener("keydown", onKey); };
  }, []);

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: "#0B0E14" }}>
      {mobileNav && <div className="fixed inset-0 z-30 bg-black/60 md:hidden" onClick={() => setMobileNav(false)} data-testid="mobile-nav-backdrop" />}
      {/* Sidebar */}
      <aside className={"fixed md:static inset-y-0 left-0 z-40 w-60 shrink-0 flex flex-col border-r border-slate-800 transform transition-transform duration-200 md:translate-x-0 " + (mobileNav ? "translate-x-0" : "-translate-x-full")} style={{ background: "#090C10" }}>
        <div className="h-14 flex items-center gap-2.5 px-4 border-b border-slate-800">
          <img src="/brand/mimoit-icon.png" alt="mimoIT" className="w-8 h-8 object-contain shrink-0" />
          <div className="leading-none">
            <div className="font-heading font-extrabold text-[15px] text-slate-100 tracking-tight">mimoIT<span style={{color:"#00F0FF"}}> Control</span></div>
            <div className="text-[9px] uppercase tracking-[0.18em] text-slate-500 mt-0.5">Technology Solutions</div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-2 px-2 space-y-0.5">
          {visibleNav.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/ /g, "-")}`} onClick={() => setMobileNav(false)}
              className={({ isActive }) =>
                "group flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-[13px] font-medium transition-colors " +
                (isActive ? "bg-cyan-500/10 text-cyan-300" : "text-slate-400 hover:bg-slate-800/60 hover:text-slate-100")}>
              <n.icon size={16} className="shrink-0" />
              <span className="flex-1">{n.label}</span>
              {n.to === "/alerts" && alertCount > 0 && (
                <span className="text-[10px] font-mono bg-rose-500/20 text-rose-300 rounded px-1.5 py-0.5">{alertCount}</span>
              )}
            </NavLink>
          ))}
        </nav>
        <button data-testid="sidebar-copilot-btn" onClick={() => setCopilotOpen(true)}
          className="m-2 flex items-center gap-2 rounded-md px-3 py-2 text-[13px] font-semibold text-slate-950 transition-transform hover:scale-[1.02]"
          style={{ background: "linear-gradient(135deg,#00F0FF,#38BDF8)" }}>
          <Sparkles size={15} /> Ask Copilot
        </button>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="h-14 shrink-0 flex items-center gap-3 px-4 border-b border-slate-800" style={{ background: "#0B0E14" }}>
          <button data-testid="mobile-nav-toggle" onClick={() => setMobileNav(true)} className="md:hidden text-slate-300 hover:text-cyan-300 p-1"><Menu size={20} /></button>
          <button data-testid="command-palette-trigger" onClick={() => setPaletteOpen(true)}
            className="hidden md:flex items-center gap-2 text-slate-400 hover:text-slate-200 bg-slate-900/70 border border-slate-800 rounded-md px-3 py-1.5 text-[13px] w-72 transition-colors">
            <Search size={14} /> <span className="flex-1 text-left">Search devices, customers…</span>
            <kbd className="text-[10px] font-mono bg-slate-800 rounded px-1.5 py-0.5 flex items-center gap-0.5"><CmdIcon size={9} />K</kbd>
          </button>
          <img src="/brand/mimoit-icon.png" alt="mimoIT" className="w-7 h-7 object-contain md:hidden" />
          <div className="flex-1" />
          <NavLink to="/alerts" className="relative text-slate-400 hover:text-slate-100 p-1.5">
            <BellRing size={18} />
            {alertCount > 0 && <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-rose-500 text-[9px] font-bold text-white flex items-center justify-center">{alertCount > 9 ? "9+" : alertCount}</span>}
          </NavLink>
          <button data-testid="mfa-header-btn" onClick={() => setMfaOpen(true)} title={user?.mfa_enabled ? "MFA enabled" : "Set up MFA"}
            className={"p-1.5 " + (user?.mfa_enabled ? "text-emerald-400" : "text-slate-400 hover:text-cyan-300")}>
            <ShieldCheck size={18} />
          </button>
          <div className="flex items-center gap-2.5 pl-3 border-l border-slate-800">
            <div className="text-right leading-none">
              <div className="text-[12px] font-semibold text-slate-200">{user?.name}</div>
              <div className="text-[10px] text-slate-500 flex items-center gap-1 justify-end">
                {user?.role_label}{user?.mfa_enabled && <ShieldCheck size={10} className="text-emerald-400" />}
              </div>
            </div>
            <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-cyan-300 font-semibold text-sm">
              {user?.name?.[0]?.toUpperCase()}
            </div>
            <button data-testid="logout-btn" onClick={logout} className="text-slate-500 hover:text-rose-400 p-1"><LogOut size={16} /></button>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto">
          {user?.mfa_enrollment_required && !user?.mfa_enabled && (
            <div data-testid="mfa-enforce-banner" className="flex items-center gap-2 px-4 py-2 bg-amber-500/10 border-b border-amber-800/60 text-amber-200 text-[13px]">
              <ShieldAlert size={16} className="shrink-0" />
              <span className="flex-1">Your organization requires multi-factor authentication. Please set it up to secure your account.</span>
              <button data-testid="mfa-banner-setup" onClick={() => setMfaOpen(true)} className="font-semibold text-amber-100 underline underline-offset-2">Set up now</button>
            </div>
          )}
          {children}
        </main>
      </div>

      <CommandPalette open={paletteOpen} setOpen={setPaletteOpen} onNavigate={(p) => nav(p)} onCopilot={() => setCopilotOpen(true)} />
      <CopilotDrawer open={copilotOpen} setOpen={setCopilotOpen} />
      <MfaSetupModal open={mfaOpen} setOpen={setMfaOpen} enabled={user?.mfa_enabled} onChange={refreshUser} />
      <TimerBanner />
    </div>
  );
}
